import { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Endpoint } from '@/types';
import { useUpdateEndpoint } from '@/hooks/useEndpoints';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useToast } from '@/contexts/ToastContext';
import { endpointDisplayPath } from '@/utils/endpointUrl';
import {
  EndpointPathInput,
  normalizeEndpointPathValue,
  validateEndpointPath,
} from './EndpointPathInput';
import { describeEndpointSaveError } from './endpointSaveError';

interface CompactEndpointConfigProps {
  endpoint: Endpoint;
  isEditing?: boolean;
  onFieldChange?: (field: string, value: any) => void;
}

export function CompactEndpointConfig({
  endpoint,
  isEditing = false,
  onFieldChange
}: CompactEndpointConfigProps) {
  const [copied, setCopied] = useState(false);
  const [isEditingPath, setIsEditingPath] = useState(false);
  const [editedPath, setEditedPath] = useState('');
  const [pathError, setPathError] = useState<string | undefined>();
  const updateEndpoint = useUpdateEndpoint();
  const toast = useToast();
  // Queued (async) endpoints are flag-gated (task 336). Flag off → static Sync.
  const queuedEnabled = useFlag('innovate.fe.endpoints.queued');
  // The exposure controls (task 850) share one rollback lever with the public
  // interface publishing section — they are one feature seen from two tabs.
  const exposureEnabled = useFlag('innovate.fe.service-exposure.manage');

  // The callable path, account-slug qualified (task 385) — this is the string
  // the copy button hands over, so it must be the real URL shape.
  const displayPath = endpointDisplayPath(endpoint);

  const handleCopyPath = async () => {
    if (endpoint.path) {
      await navigator.clipboard.writeText(displayPath);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleStartEditPath = () => {
    setEditedPath(normalizeEndpointPathValue(endpoint.path || ''));
    setPathError(undefined);
    setIsEditingPath(true);
  };

  const handleSavePath = async () => {
    // Task 388: this editor used to `return` silently on an empty value or a
    // missing leading slash — the User pressed save and nothing happened. Now
    // every rejection is visible, and the leading slash is normalized rather
    // than demanded.
    const localError = validateEndpointPath(editedPath);
    if (localError) {
      setPathError(localError);
      return;
    }

    const normalizedPath = normalizeEndpointPathValue(editedPath);
    if (normalizedPath === normalizeEndpointPathValue(endpoint.path || '')) {
      setIsEditingPath(false);
      setPathError(undefined);
      return;
    }

    try {
      await updateEndpoint.mutateAsync({
        id: endpoint.id,
        data: { path: normalizedPath }
      });
      setIsEditingPath(false);
      setPathError(undefined);
    } catch (error) {
      const display = describeEndpointSaveError(error, 'Failed to update path');
      if (display.pathError) {
        setPathError(display.pathError);
      } else {
        setPathError(display.message);
        toast.error(display.message);
      }
    }
  };

  const handleCancelEditPath = () => {
    setIsEditingPath(false);
    setEditedPath('');
    setPathError(undefined);
  };

  const handlePathKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSavePath();
    } else if (e.key === 'Escape') {
      handleCancelEditPath();
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Request</h2>

      <div className="space-y-3">
        {/* Row 1: Method (fixed width) + Path (fluid) */}
        <div className="flex items-end gap-4">
          <div className="w-20 flex-shrink-0">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Method</div>
            {isEditing ? (
              <select
                value={endpoint.method}
                onChange={(e) => onFieldChange?.('method', e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            ) : (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                {endpoint.method}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Path</div>
            {isEditing ? (
              /* Bulk edit — the same validated component as the inline editor
                 and the create form (task 388). The parent normalizes on save. */
              <EndpointPathInput
                value={endpoint.path || ''}
                onChange={(next) => onFieldChange?.('path', next)}
                accountSlug={endpoint.accountSlug}
                error={validateEndpointPath(endpoint.path || '') ?? undefined}
                compact
                hideLabel
                hideHelpText
              />
            ) : isEditingPath ? (
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <EndpointPathInput
                    value={editedPath}
                    onChange={(next) => {
                      setEditedPath(next);
                      if (pathError) setPathError(undefined);
                    }}
                    accountSlug={endpoint.accountSlug}
                    error={pathError}
                    onKeyDown={handlePathKeyDown}
                    disabled={updateEndpoint.isPending}
                    autoFocus
                    compact
                    hideLabel
                    hideHelpText
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSavePath}
                  disabled={updateEndpoint.isPending}
                  className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                  title="Save"
                >
                  <CheckIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={handleCancelEditPath}
                  disabled={updateEndpoint.isPending}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Cancel"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <code className="text-sm bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-2 py-1 rounded flex-1 font-mono">
                  {displayPath}
                </code>
                <button
                  type="button"
                  onClick={handleStartEditPath}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Edit path"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={handleCopyPath}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <CheckIcon className="w-4 h-4 text-green-500" />
                  ) : (
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Queued (fixed) + Format (fixed) */}
        <div className="flex items-start gap-6">
          <div className="w-24 flex-shrink-0">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Queued</div>
            {!queuedEnabled ? (
              <span className="text-sm text-gray-900 dark:text-white">Sync</span>
            ) : isEditing ? (
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={endpoint.responseQueued}
                  onChange={(e) => onFieldChange?.('responseQueued', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                  (Async)
                </span>
              </label>
            ) : (
              <span className="text-sm text-gray-900 dark:text-white">
                {endpoint.responseQueued ? 'Yes (Async)' : 'No (Sync)'}
              </span>
            )}
          </div>

          <div className="w-40 flex-shrink-0">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Format</div>
            {isEditing ? (
              <input
                type="text"
                value={endpoint.requestFormat}
                onChange={(e) => onFieldChange?.('requestFormat', e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="application/json"
              />
            ) : (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                {endpoint.requestFormat}
              </span>
            )}
          </div>

          {/* The chat pattern (task 749) — whether calls to this AI Service
              belong to a durable, resumable conversation. Sits with the other
              call-shape properties because that is what it changes. */}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Conversations
            </div>
            {isEditing ? (
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={endpoint.threadsEnabled ?? false}
                  onChange={(e) => onFieldChange?.('threadsEnabled', e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                  Persist and resume
                </span>
              </label>
            ) : (
              <span className="text-sm text-gray-900 dark:text-white">
                {endpoint.threadsEnabled
                  ? 'On (conversations persist and resume)'
                  : 'Off (each call is stateless)'}
              </span>
            )}
          </div>

          {/* MCP exposure (task 850). The field has existed since the service
              had endpoints, but the only control for it was the create form —
              so an AI Service could never be added to (or removed from) the
              account's MCP tool catalog after it existed. The Developer Info
              tab describes the result and points here; this is the switch. */}
          {exposureEnabled && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">MCP</div>
              {isEditing ? (
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={endpoint.mcpExposed ?? false}
                    onChange={(e) => onFieldChange?.('mcpExposed', e.target.checked)}
                    data-testid="service-mcp-exposed-toggle"
                    className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">
                    Expose as an MCP tool
                  </span>
                </label>
              ) : (
                <span className="text-sm text-gray-900 dark:text-white">
                  {endpoint.mcpExposed
                    ? 'On (in the account MCP tool catalog)'
                    : 'Off (not offered as a tool)'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Task 784 removed two things that used to close this card:
            the Collaboration Config row — a link into Advanced internals from a
            product-focused page, and the Config tab is that wiring's home — and
            the billing banner, which put a plan-tier caveat in front of a User
            reading their own service's shape. Neither belonged on Overview. */}
      </div>
    </div>
  );
}
